import React, { useEffect, useRef, useMemo, useState } from "react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "~/components/ui/custom-radix-components/accordion";

type PropertyDetails = {
    addressFormula?: string;
};

type SubSection = {
    id: string;
    title?: string;
    heading?: string;
};

type Section = {
    id: string;
    heading?: string | null;
    subHeading?: string | null;
    propertyDetails?: PropertyDetails | null;
    workspaceProviderName?: string | null;
    blogTitle?: string | null;
    blogId?: string;
    subSections?: SubSection[];
};

interface TOCAccordionProps {
    tableOfContentHeading?: string;
    sections: Section[];
    activeId?: string;
}

const TOCAccordion: React.FC<TOCAccordionProps> = ({
    tableOfContentHeading = "Table of Contents",
    sections,
    activeId,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [openAccordion, setOpenAccordion] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!activeId || !containerRef.current) return;
        const activeEl = containerRef.current.querySelector(
            `[data-id="${activeId}"]`
        ) as HTMLElement | null;
        if (activeEl) {
            activeEl.scrollIntoView({
                block: "nearest",
                inline: "nearest",
                behavior: "smooth",
            });
        }
    }, [activeId]);

    const scrollToSection = (sectionId: string, offset = 80) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const y = element.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top: y, behavior: "smooth" });
        }
    };
    const handleSectionClick = (sectionId: string) => {
        scrollToSection(sectionId);
    };

    const groupedSections = useMemo(() => {
        const result: {
            id: string;
            heading?: string | null;
            subHeading?: string | null;
            propertyList?: Section[];
            subSections: {
                id: string;
                title?: string;
                propertyList: Section[];
            }[];
        }[] = [];

        let currentGroup: any = null;
        let currentSubSection: any = null;

        for (const section of sections) {
            if (section.heading) {
                currentGroup = {
                    id: section.id,
                    heading: section.heading,
                    subHeading: null,
                    propertyList: [],
                    subSections: section.subSections?.length
                        ? section.subSections.map((sub) => ({
                            id: sub.id,
                            title: sub.title,
                            propertyList: [],
                        }))
                        : [],
                };
                result.push(currentGroup);
                currentSubSection = null;
                continue;
            }

            if (section.subHeading) {
                if (!currentGroup) {
                    currentGroup = {
                        id: section.blogId,
                        heading: section.blogTitle || "Default",
                        subHeading: null,
                        propertyList: [],
                        subSections: [],
                    };
                    result.push(currentGroup);
                }

                currentSubSection = {
                    id: section.id,
                    title: section.subHeading,
                    propertyList: [],
                };
                currentGroup.subSections.push(currentSubSection);
                continue;
            }

            if (section.propertyDetails) {
                if (!currentGroup) {
                    currentGroup = {
                        id: section.blogId || "default-group",
                        heading: section.blogTitle || "Default",
                        subHeading: null,
                        propertyList: [],
                        subSections: [],
                    };
                    result.push(currentGroup);
                }

                if (currentSubSection) {
                    currentSubSection.propertyList.push(section);
                } else {
                    if (!currentGroup.propertyList) currentGroup.propertyList = [];
                    currentGroup.propertyList.push(section);
                }
            }
        }

        return result;
    }, [sections]);

    useEffect(() => {
        if (!activeId) return;

        const matchByProperty = groupedSections.find((group) =>
            group.subSections.some((sub) =>
                sub.propertyList.some((p) => p.id === activeId)
            )
        );

        const matchByDirectProperty = groupedSections.find((group) =>
            group.propertyList?.some((p) => p.id === activeId)
        );

        const matchBySubSection = groupedSections.find((group) =>
            group.subSections?.some((s) => s.id === activeId)
        );

        if (matchByProperty) {
            setOpenAccordion(matchByProperty.id);
        } else if (matchByDirectProperty) {
            setOpenAccordion(matchByDirectProperty.id);
        } else if (matchBySubSection) {
            setOpenAccordion(matchBySubSection.id);
        } else {
            const isGroupHeading = groupedSections.some(
                (group) => group.id === activeId
            );
            if (isGroupHeading) {
                setOpenAccordion(activeId);
            }
        }
    }, [activeId, groupedSections]);

    return (
        <div ref={containerRef} className="my-4 mr-4 sticky top-20 max-h-[80vh] overflow-y-auto pr-4
         scrollbar-default scrollbar-thin scrollbar-thumb scrollbar-track
        ">

            <p className="text-2xl font-semibold text-gray-900 mb-2 pb-2 uppercase">
                {tableOfContentHeading}
            </p>

            <Accordion
                type="single"
                collapsible
                value={openAccordion}
                onValueChange={setOpenAccordion}
                className="w-full">
                {/* 🏷️ Blog title at top (scroll to top) */}
                {!sections[0].propertyDetails &&
                    !sections[0].title &&
                    sections[0].blogTitle && (
                        <div
                            className={`w-full max-w-full text-left text-sm font-normal text-black
                        hover:no-underline hover:bg-gray-100 transition py-3 px-2 mb-1 rounded cursor-pointer
                        ${activeId === sections[0]?.blogId ? "bg-gray-100 font-semibold" : ""}`}
                            title={sections[0]?.blogTitle}
                            data-id={sections[0]?.blogId}
                            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        >
                            <span className="block w-full truncate">{sections[0]?.blogTitle}</span>
                        </div>
                    )}

                {/*  Render grouped sections */}
                {groupedSections.map((group) => {
                    const hasSubSections = group.subSections.length > 0;
                    const hasProperties = group.propertyList && group.propertyList.length > 0;
                    const groupLabel = group.heading || group.subHeading || "";

                    return (
                        <AccordionItem key={group.id} value={group.id} data-id={group.id}>
                            {hasSubSections || hasProperties ? (
                                <AccordionTrigger
                                    className={`tw-full max-w-full text-left text-sm font-normal text-black 
    hover:no-underline hover:bg-gray-100 transition py-3 px-2 rounded cursor-pointer
    ${activeId === group.id ? "bg-gray-100 font-semibold" : ""}`}
                                    onClick={() => {
                                        if (group.id === sections[0]?.blogId) {
                                            window.scrollTo({ top: 0, behavior: "smooth" });
                                        } else {
                                            handleSectionClick(group.id);
                                        }
                                    }}
                                >
                                    <span className="block w-full truncate">
                                        {groupLabel}
                                        {/* If you want icon, add here */}
                                        {/* <ChevronDown className='...' /> */}
                                    </span>
                                </AccordionTrigger>
                            ) : (
                                <div
                                    className={`text-left text-sm font-normal text-black 
                                hover:no-underline hover:bg-gray-100 transition py-3 px-2 rounded cursor-pointer
                                ${activeId === group.id ? "bg-gray-100 font-semibold" : ""}`}
                                    onClick={() => handleSectionClick(group.id)}
                                    data-id={group.id}
                                    title={groupLabel}
                                >
                                    <span className="block w-full truncate">{groupLabel}</span>
                                </div>
                            )}

                            {/* Sub-sections + direct properties */}
                            {(hasSubSections || hasProperties) && (
                                <AccordionContent>
                                    {group.subSections.map((sub) => {
                                        const hasSubProps =
                                            sub.propertyList && sub.propertyList.length > 0;

                                        return (
                                            <div key={sub.id}>
                                                {sub.title && (
                                                    <div
                                                        className={`w-full max-w-full text-left text-sm font-normal text-black
                                                    hover:no-underline hover:bg-gray-100 transition
                                                    py-3 pl-4 pr-2 my-1 rounded cursor-pointer
                                                    ${activeId === sub.id ? "bg-gray-100 font-semibold" : ""}`}
                                                        onClick={() => handleSectionClick(sub.id)}
                                                        data-id={sub.id}
                                                        title={sub.title}
                                                    >
                                                        <div className="flex items-start">
                                                            <div className="min-w-0 truncate">{sub.title}</div>
                                                        </div>
                                                    </div>
                                                )}

                                                {hasSubProps &&
                                                    sub.propertyList.map((property) => {
                                                        const label = property.workspaceProviderName
                                                            ? `${property.workspaceProviderName} - ${property.propertyDetails?.addressFormula}`
                                                            : property.propertyDetails?.addressFormula;

                                                        return (
                                                            <div
                                                                key={property.id}
                                                                className={`w-full max-w-full text-left text-sm font-normal text-black
                                                            hover:no-underline hover:bg-gray-100 transition
                                                            py-3 pl-4 pr-2 my-1 rounded cursor-pointer
                                                            ${activeId === property.id ? "bg-gray-100 font-semibold" : ""}`}
                                                                onClick={() =>
                                                                    handleSectionClick(property.id)
                                                                }
                                                                data-id={property.id}
                                                                title={label}
                                                            >
                                                                <div className="flex items-start">
                                                                    <div className="min-w-0">{label}</div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        );
                                    })}

                                    {/* Direct property list under group */}
                                    {hasProperties &&
                                        group.propertyList?.map((property) => {
                                            const label = property.workspaceProviderName
                                                ? `${property.workspaceProviderName} - ${property.propertyDetails?.addressFormula}`
                                                : property.propertyDetails?.addressFormula;

                                            return (
                                                <div
                                                    key={property.id}
                                                    className={`w-full max-w-full text-left text-sm font-normal text-black
                                                hover:no-underline hover:bg-gray-100 transition
                                                py-3 pl-4 pr-2 my-1 rounded cursor-pointer
                                                ${activeId === property.id ? "bg-gray-100 font-semibold" : ""}`}
                                                    onClick={() =>
                                                        handleSectionClick(property.id)
                                                    }
                                                    data-id={property.id}
                                                    title={label}
                                                >
                                                    <div className="flex items-start">
                                                        <div className="min-w-0">{label}</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </AccordionContent>
                            )}
                        </AccordionItem>
                    );
                })}
            </Accordion>
        </div>
    );
};

export default TOCAccordion;
